import React, { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../app/hooks";
import AcedSelect, {
	SelectOption,
} from "../../../components/essentials/input/select/AcedSelect";
import AcedInput from "../../../components/essentials/input/text/AcedInput";
import { selectUser, setUser } from "../../../core/auth/authSlice";
import { User } from "../../../shared/models";
import http from "../../../shared/utils/http";
import { capitalizeFirstLetter } from "../../../shared/utils/util";
import "./CompleteSportsInfo.scss";

const CompleteSportsInfo: React.FC = () => {
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { accountType } = useSelector(selectUser) as User;
	const user = useSelector(selectUser) as User;

	const [positions, setPositions] = useState<SelectOption[]>([]);
	const [allSports, setAllSports] = useState<SelectOption[]>([]);
	const [categories, setCategories] = useState<SelectOption[]>([]);

	const selectedSport = useWatch({ control, name: "sport" });

	useEffect(() => {
		if (accountType === undefined) navigate("/type");

		// Load all sports on mount
		http.get("/utils/sports").then((response) => {
			setAllSports(
				response.map(
					(sport: { name: string; value: string }) =>
						({
							value: sport.value,
							label: t(
								"sportData.sportsLabels." + sport.value,
								capitalizeFirstLetter(sport.name)
							),
						} as SelectOption)
				)
			);
		});
	}, [accountType, navigate, t]);

	// Generate categories based on selected sport (Male/Female + Sport)
	useEffect(() => {
		if (!selectedSport) {
			setCategories([]);
			return;
		}

		const sportLabel = t(
			"sportData.sportsLabels." + selectedSport,
			capitalizeFirstLetter(selectedSport)
		);
		setCategories([
			{ value: "M", label: `Male ${sportLabel}` },
			{ value: "F", label: `Female ${sportLabel}` },
		]);
	}, [selectedSport, t]);

	const onSubmit = (data: any) => {
		let body: any = {
			sport: data.sport,
		};

		if (["medStaff", "techStaff", "management"].includes(accountType)) {
			body["position"] = data.position;
		}

		if (accountType === "athlete") {
			if (data.category) {
				body["category"] = data.category;
			}
			if (data.sport === "volleyball") {
				body["position"] = data.position;
				body["height"] = Number(data.height);
			}
		}

		http
			.patch(`/members/member`, body)
			.then((response) => {
				dispatch(setUser({ ...user, ...response }));
				navigate("/pic");
			})
			.catch((error) => console.log(error));
	};

	const validateHeight: (height: number) => string | undefined = (
		height: number
	) => {
		return height < 130 || height > 220 || Number.isNaN(height)
			? t("profile.profileData.errors.wrongHeight")
			: undefined;
	};

	// Load volleyball positions when volleyball is selected
	useEffect(() => {
		if (
			selectedSport === "volleyball" &&
			accountType === "athlete" &&
			positions.length === 0
		) {
			http.get("/utils/positions?sport=volleyball").then((response) => {
				setPositions(
					response.map((position: { name: string; value: string }) => {
						return {
							value: position.value,
							label: t(
								`sportData.positions.volleyball.${position.value}`,
								position.name
							),
						} as SelectOption;
					})
				);
			});
		}
	}, [selectedSport, accountType, positions.length, t]);

	const [staffRoles, setStaffRoles] = useState<SelectOption[]>();
	useEffect(() => {
		if (["medStaff", "techStaff", "management"].includes(accountType))
			http.get("/utils/staff?type=" + accountType).then((response) => {
				setStaffRoles(
					response.map(
						(value: string) =>
							({
								value,
								label: t("sportData.positions.staff." + value),
							} as SelectOption)
					)
				);
			});
	}, [accountType, t]);

	const renderAdditionalFields = () => {
		if (accountType === "athlete" && selectedSport === "volleyball") {
			return (
				<>
					{/* Category select for volleyball athletes */}
					<div className="d-flex column center w-100">
						<AcedSelect
							label={t("profile.sportsInfo.category", "Category")}
							className="w-100"
							control={control}
							options={categories}
							placeholder={t(
								"profile.sportsInfo.selectCategory",
								"Select Category"
							)}
							name="category"
							isDisabled={!selectedSport}
							rules={{
								required: t(
									"profile.sportsInfo.errors.categoryRequired",
									"Category is required"
								),
							}}
						/>
					</div>

					{/* Position and height for volleyball athletes */}
					<AcedSelect
						label={t("profile.sportsInfo.position", "Position")}
						control={control}
						options={positions}
						placeholder={t("profile.sportsInfo.position", "Position")}
						name="position"
						rules={{
							required: t(
								"profile.sportsInfo.errors.positionRequired",
								"Position is required"
							),
						}}
					/>
					<AcedInput
						name="height"
						placeholder={t("profile.profileData.formFieldsLabels.height")}
						control={control}
						rules={{
							required: t(
								"profile.sportsInfo.errors.heightRequired",
								"Height is required"
							),
							validate: (value) => validateHeight(Number(value)),
						}}
					/>
				</>
			);
		}
		return null;
	};

	return (
		<div className="complete-sports-info">
			<p className="complete-sports-info__caption">
				{t("profile.sportsInfo.title", "Sports Information")}
			</p>

			<form
				id="complete-sports-info-form"
				className="complete-sports-info__form"
				onSubmit={handleSubmit(onSubmit)}
			>
				{["medStaff", "techStaff", "management"].includes(accountType) &&
					staffRoles && (
						<div className="d-flex column center w-100">
							<AcedSelect
								label={t("profile.sportsInfo.staffPosition", "Staff Position")}
								control={control}
								options={staffRoles}
								placeholder={t("profile.sportsInfo.selectRole", "Select Role")}
								name="position"
								rules={{
									required: t(
										"profile.sportsInfo.errors.positionRequired",
										"Position is required"
									),
								}}
							/>
						</div>
					)}

				<div className="d-flex column center w-100">
					<AcedSelect
						label={t("profile.sportsInfo.sport", "Sport")}
						className="w-100"
						control={control}
						options={allSports}
						placeholder={t("profile.sportsInfo.selectSport", "Select Sport")}
						name="sport"
						rules={{
							required: t(
								"profile.sportsInfo.errors.sportRequired",
								"Sport is required"
							),
						}}
					/>
				</div>
				{renderAdditionalFields()}
			</form>
		</div>
	);
};

export default CompleteSportsInfo;
